import { PAYMENT_BEHAVIOR } from "@/helpers/order/payment/behavior";
import {
  serializeDejavooProcessorResponse,
  getDejavooFeeAmount,
} from "@/helpers/payments/Dejavoo";
import { makePaymentResult } from "../paymentResult";

function firstExtendedAppBlock(data) {
  const byApp = data && data.ExtendedDataByApplication;
  if (!byApp || typeof byApp !== "object") return null;
  const vals = Object.values(byApp);
  return vals.length ? vals[0] : null;
}

/** card_lastfour resolution matching the legacy pay() branch. */
function resolveCardLastFour(data) {
  if (data && data.CardData) {
    return data.CardData.Last4 || "";
  }
  if (data && data.ExtendedDataByApplication) {
    const firstApp = firstExtendedAppBlock(data);
    return (firstApp && firstApp.AcntLast4) || "";
  }
  return undefined;
}

/** Extra card / auth metadata used by the partial-approval payment record. */
function resolveCardMeta(data) {
  const meta = {};
  if (data && data.CardData) {
    meta.card_lastfour = data.CardData.Last4 || "";
    meta.card_type = data.CardData.CardType || "";
    meta.entry_type = data.CardData.EntryType || "";
    meta.card_first4 = data.CardData.First4 || "";
    meta.card_bin = data.CardData.BIN || "";
    meta.card_expiration = data.CardData.ExpirationDate || "";
    meta.cardholder_name = data.CardData.Name || "";
  }
  if (data) {
    meta.auth_code = data.AuthCode || "";
    meta.transaction_type = data.TransactionType || "";
    meta.reference_id = data.ReferenceId || "";
    meta.approval_message = data.GeneralResponse
      ? data.GeneralResponse.DetailedMessage || data.GeneralResponse.Message || ""
      : "";
  }
  return meta;
}

/**
 * Dejavoo integration processor.
 *
 * Consumes the raw JSON response returned by the checkout's
 * `DejavooPaymentProcessing` (which wraps makeDejavooPayment/Refund). Side
 * effects (tips_total mutation, surcharge application, partial-approval modal,
 * payment record persistence) stay in the component; this only translates the
 * raw response into the normalized shape.
 */
export const dejavooProcessor = {
  behaviors: [PAYMENT_BEHAVIOR.DEJAVOO_INTEGRATION],

  normalize(data) {
    if (!data) {
      return makePaymentResult({ approved: false, processorResponse: "no data", raw: data });
    }

    const amounts = data.Amounts || {};
    const tipAmount = amounts.TipAmount != null ? parseFloat(amounts.TipAmount) : NaN;
    const approvedAmount =
      amounts.ApprovedAmount != null ? parseFloat(amounts.ApprovedAmount) : NaN;
    const terminalFee = getDejavooFeeAmount(data);

    return makePaymentResult({
      approved: true,
      processorResponse: serializeDejavooProcessorResponse(data),
      cardLastFour: resolveCardLastFour(data),
      cardMeta: resolveCardMeta(data),
      tips: Number.isFinite(tipAmount) && tipAmount > 0 ? tipAmount : null,
      terminalFee: terminalFee > 0 ? terminalFee : null,
      isPartialApproval: !!data.isPartialApproval,
      approvedAmount: Number.isFinite(approvedAmount) ? approvedAmount : 0,
      raw: data,
    });
  },
};
